import React, { useState } from 'react';
import { Search, X, Filter as FilterIcon } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { SearchWithAutoComplete } from '../search/SearchWithAutoComplete';
import { AdvancedSearchDialog, AdvancedSearchCriteria } from '../search/AdvancedSearchDialog';
import { getFilterTagClassName } from '../../utils/tagColors';

interface Category {
  id: number;
  name: string;
  description?: string;
  color: string;
  createdAt: string;
}

interface Tag {
  id: number;
  name: string;
  createdAt: string;
}

export interface FilterBarProps {
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  selectedCategory?: number;
  onCategoryChange: (id: number | undefined) => void;
  categories: Category[];
  selectedTags: string[];
  onToggleTag: (tagName: string) => void;
  availableTags: Tag[];
  onClearAll: () => void;
  className?: string;
  compact?: boolean;
  extraControls?: React.ReactNode;
  // Advanced search support
  onAdvancedSearch?: (criteria: AdvancedSearchCriteria) => void;
  advancedSearchCriteria?: AdvancedSearchCriteria;
  recentSearches?: string[];
  onRecentSearchClick?: (search: string) => void;
  onClearRecentSearches?: () => void;
  enableAdvancedSearch?: boolean;
  onSaveSearch?: (name: string, description: string, criteria: AdvancedSearchCriteria) => void;
}

/**
 * FilterBar consolidates search input, category selector, and tag multi-select.
 * - Responsive: stacks on small screens, horizontal grouping on large.
 * - Accessible: labels, aria descriptions, keyboard focus styles via Tailwind.
 * - Now supports advanced search dialog and real-time search with autocomplete.
 */
export const FilterBar: React.FC<FilterBarProps> = ({
  searchTerm,
  onSearchTermChange,
  selectedCategory,
  onCategoryChange,
  categories,
  selectedTags,
  onToggleTag,
  availableTags,
  onClearAll,
  className,
  compact,
  extraControls,
  onAdvancedSearch,
  advancedSearchCriteria,
  recentSearches = [],
  onRecentSearchClick,
  onClearRecentSearches,
  enableAdvancedSearch = true,
  onSaveSearch
}) => {
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [localAdvancedCriteria, setLocalAdvancedCriteria] = useState<AdvancedSearchCriteria>(
    advancedSearchCriteria || {
      searchTerm,
      categoryIds: selectedCategory ? [selectedCategory] : [],
      tags: selectedTags
    }
  );

  // Derive popular tags (simple heuristic: earliest created or alphabetical fallback)
  const popularTags = React.useMemo(() => {
    return [...availableTags]
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 16);
  }, [availableTags]);

  // Check if we have any advanced filters active beyond basic search
  const hasAdvancedFilters = React.useMemo(() => {
    if (!advancedSearchCriteria) return false;
    
    return !!(
      advancedSearchCriteria.createdAfter ||
      advancedSearchCriteria.createdBefore ||
      advancedSearchCriteria.minUses ||
      advancedSearchCriteria.maxUses ||
      advancedSearchCriteria.minLikes ||
      advancedSearchCriteria.maxLikes ||
      advancedSearchCriteria.minContentLength ||
      advancedSearchCriteria.maxContentLength ||
      advancedSearchCriteria.submittedBy ||
      advancedSearchCriteria.excludeTags?.length ||
      advancedSearchCriteria.exactMatch ||
      advancedSearchCriteria.scope !== 'all'
    );
  }, [advancedSearchCriteria]);

  const handleBasicSearch = (term: string) => {
    onSearchTermChange(term);
  };

  const handleAdvancedSearchOpen = () => {
    // Sync current basic filters to advanced search
    setLocalAdvancedCriteria({
      ...advancedSearchCriteria,
      searchTerm,
      categoryIds: selectedCategory ? [selectedCategory] : [],
      tags: selectedTags
    });
    setShowAdvancedSearch(true);
  };

  const handleAdvancedSearchApply = () => {
    if (onAdvancedSearch) {
      onAdvancedSearch(localAdvancedCriteria);
    }
  };

  const handleAdvancedSearchReset = () => {
    const resetCriteria: AdvancedSearchCriteria = {
      scope: 'all',
      categoryMode: 'any',
      tagMode: 'any',
      exactMatch: false,
      includeFacets: false
    };
    setLocalAdvancedCriteria(resetCriteria);
    if (onAdvancedSearch) {
      onAdvancedSearch(resetCriteria);
    }
  };

  return (
    <div className={cn('rounded-lg border bg-white shadow-xs p-4 md:p-5 space-y-5', className)}>
      {/* Row 1: Search + Advanced Search + Clear + (optional) extra controls */}
      <div className={cn('flex flex-col gap-3 md:flex-row md:items-center', compact && 'md:gap-2')}>        
        <div className="relative flex-1 min-w-[240px]">
          {enableAdvancedSearch ? (
            <SearchWithAutoComplete
              value={searchTerm}
              onChange={handleBasicSearch}
              onSearch={handleBasicSearch}
              placeholder="Search prompts, tags, or content..."
              recentSearches={recentSearches}
              onRecentSearchClick={onRecentSearchClick}
              onClearRecent={onClearRecentSearches}
            />
          ) : (
            <>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" aria-hidden="true" />
              <Input
                value={searchTerm}
                onChange={(e) => onSearchTermChange(e.target.value)}
                placeholder="Search by title, text, or submitter..."
                aria-label="Search prompts"
                className="pl-10"
              />
            </>
          )}
        </div>

        <div className="flex gap-2 items-center flex-wrap">
          {/* Advanced Search Button */}
          {enableAdvancedSearch && (
            <Button
              type="button"
              variant={hasAdvancedFilters ? "default" : "outline"}
              onClick={handleAdvancedSearchOpen}
              className={cn(
                "flex items-center gap-2",
                hasAdvancedFilters && "bg-blue-600 text-white hover:bg-blue-700"
              )}
              aria-label="Open advanced search"
            >
              <FilterIcon className="w-4 h-4" />
              {hasAdvancedFilters ? "Advanced*" : "Advanced"}
            </Button>
          )}

          {/* Category Filter */}
          <select
            value={selectedCategory || ''}
            onChange={(e) => onCategoryChange(e.target.value ? Number(e.target.value) : undefined)}
            aria-label="Filter by category"
            className="px-3 py-2 border border-slate-300 rounded-md bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[180px]"
          >
            <option value="">All Categories</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <Button
            type="button"
            variant="ghost"
            onClick={onClearAll}
            className="text-slate-600 hover:text-slate-900"
            aria-label="Clear all filters"
          >
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>

          {extraControls && (
            <div className="flex items-center gap-3 ml-auto">{extraControls}</div>
          )}
        </div>
      </div>

      {/* Row 2: Tags */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tags</h3>
          {selectedTags.length > 0 && (
            <div className="text-xs text-slate-500">{selectedTags.length} selected</div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {popularTags.map(tag => {
            const active = selectedTags.includes(tag.name);
            return (
              <button
                key={tag.id}
                type="button"
                onClick={() => onToggleTag(tag.name)}
                className={getFilterTagClassName(tag.name, active)}
                aria-pressed={active}
              >
                {tag.name}
              </button>
            );
          })}
          {popularTags.length === 0 && (
            <span className="text-xs text-slate-500">No tags available</span>
          )}
        </div>
        {availableTags.length > popularTags.length && (
          <details className="mt-2 group">
            <summary className="cursor-pointer text-xs text-blue-600 hover:text-blue-700 select-none">Show more tags</summary>
            <div className="mt-2 flex flex-wrap gap-2">
              {availableTags.slice(popularTags.length).map(tag => {
                const active = selectedTags.includes(tag.name);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => onToggleTag(tag.name)}
                    className={cn(
                      'px-3 h-7 rounded-full border text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1',
                      active
                        ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                    )}
                    aria-pressed={active}
                  >
                    {tag.name}
                  </button>
                );
              })}
            </div>
          </details>
        )}

        {/* Selected tags list with remove buttons */}
        {selectedTags.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
            {selectedTags.map(tag => (
              <button
                key={tag}
                type="button"
                onClick={() => onToggleTag(tag)}
                className="group flex items-center gap-1 px-2.5 h-7 rounded-md bg-slate-100 text-slate-700 text-xs hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label={`Remove tag ${tag}`}
              >
                <span>{tag}</span>
                <X className="w-3 h-3 text-slate-500 group-hover:text-slate-700" aria-hidden="true" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Advanced Search Dialog */}
      {enableAdvancedSearch && (
        <AdvancedSearchDialog
          open={showAdvancedSearch}
          onOpenChange={setShowAdvancedSearch}
          criteria={localAdvancedCriteria}
          onCriteriaChange={setLocalAdvancedCriteria}
          categories={categories}
          availableTags={availableTags}
          onSearch={handleAdvancedSearchApply}
          onReset={handleAdvancedSearchReset}
          onSaveSearch={(name, description) => {
            onSaveSearch?.(name, description, localAdvancedCriteria);
          }}
        />
      )}
    </div>
  );
};
